import Box from '@mui/material/Box';
import { object } from 'prop-types';
import React, { memo } from 'react';

import NamedLink from '../../components/Links/NamedLink/NamedLink';
import { FormattedMessage } from '../../util/reactIntl';
import { LISTING_STATE_CLOSED, LISTING_STATE_DRAFT, LISTING_STATE_PENDING_APPROVAL } from '../../util/types';
import EditIcon from './EditIcon';
import {
  LISTING_PAGE_EDIT_ICON,
  LISTING_PAGE_EDIT_LISTING_LINK,
  ListingPageGlobalStyles,
} from './listingPageStyles';

// former ListingPage.module.css .actionBar
const actionBarSx = (theme) => ({
  position: 'absolute',
  display: 'flex',
  justifyContent: 'space-between',
  width: 'calc(100%)',
  color: theme.tokens.matterColorNegative,
  backgroundColor: theme.tokens.matterColor,
  zIndex: 1 /* bring on top of mobile image */,
  // Action bar prevents the image click events going to the parent and
  // should not show a pointer
  cursor: 'initial',
  borderRadius: theme.tokens.borderRadius,
});

// former ListingPage.module.css .ownListingText
// (@apply --marketplaceDefaultFontStyles + own md override; both have a min-width:900px block → merged)
const ownListingTextSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  margin: '14px 12px 11px 24px',
  [theme.breakpoints.up('md')]: {
    ...theme.mixins.marketplaceDefaultFontStyles['@media (min-width:900px)'],
    margin: '25px 12px 22px 24px',
  },
});

// former ListingPage.module.css .ownListingTextPendingApproval
const ownListingTextPendingApprovalSx = (theme) => ({
  color: theme.tokens.attentionColor,
});

// former ListingPage.module.css .closedListingText
// (@apply --marketplaceDefaultFontStyles + own md override; both have a min-width:900px block → merged)
const closedListingTextSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  margin: '14px 12px 11px 24px',
  textAlign: 'center',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    ...theme.mixins.marketplaceDefaultFontStyles['@media (min-width:900px)'],
    margin: '25px 12px 22px 24px',
  },
});

/**
 * When its own listing, show a message and a link to edit the listing.
 * @param {object} props
 * @param {boolean} props.isOwnListing
 * @param {import('@vansite/ts-sharetribe-flex-sdk').Listing} props.listing
 * @param {object} props.editParams
 * @param {string} props.editParams.id
 * @param {string} props.editParams.slug
 * @param {string} props.editParams.type
 * @param {string} props.editParams.tab
 * @returns {React.JSX.Element|null}
 * @constructor
 */

const ActionBarMaybeComponent = ({ isOwnListing, listing, editParams }) => {
  const state = listing.attributes.state;
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isClosed = state === LISTING_STATE_CLOSED;
  const isDraft = state === LISTING_STATE_DRAFT;

  if (isOwnListing) {
    let ownListingTextTranslationId = 'ListingPage.ownListing';

    if (isPendingApproval) {
      ownListingTextTranslationId = 'ListingPage.ownListingPendingApproval';
    } else if (isClosed) {
      ownListingTextTranslationId = 'ListingPage.ownClosedListing';
    } else if (isDraft) {
      ownListingTextTranslationId = 'ListingPage.ownListingDraft';
    }

    const message = isDraft ? 'ListingPage.finishListing' : 'ListingPage.editListing';

    return (
      <Box sx={actionBarSx}>
        <ListingPageGlobalStyles />
        <Box
          component="p"
          sx={[ownListingTextSx, isPendingApproval && ownListingTextPendingApprovalSx].filter(
            Boolean,
          )}
        >
          <FormattedMessage id={ownListingTextTranslationId} />
        </Box>
        <NamedLink
          className={LISTING_PAGE_EDIT_LISTING_LINK}
          name="EditListingPage"
          params={editParams}
        >
          <EditIcon className={LISTING_PAGE_EDIT_ICON} />
          <FormattedMessage id={message} />
        </NamedLink>
      </Box>
    );
  } else if (isClosed) {
    return (
      <Box sx={actionBarSx}>
        <ListingPageGlobalStyles />
        <Box component="p" sx={closedListingTextSx}>
          <FormattedMessage id="ListingPage.closedListing" />
        </Box>
      </Box>
    );
  }
  return null;
};

ActionBarMaybeComponent.displayName = 'ActionBarMaybe';

const ActionBarMaybe = memo(ActionBarMaybeComponent);

export default ActionBarMaybe;
